/**
 * AutoElite Shared Data Store
 * localStorage keys used across both customer and admin apps.
 *
 * Invoice flow:
 *   Admin creates invoice → stored in ae_invoices
 *   Customer app reads ae_invoices by bookingId
 *   Customer pays → ae_payments record created
 *   Customer leaves feedback → ae_feedback record created
 *   Feedback feeds into testimonials on home page
 */

const KEYS = {
  invoices:  'ae_invoices',   // { [bookingId]: InvoiceRecord }
  payments:  'ae_payments',   // { [invoiceId]: PaymentRecord }
  feedback:  'ae_feedback',   // [FeedbackRecord, ...]
};

function _load(key) {
  try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : null; }
  catch { return null; }
}
function _save(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

// ── INVOICES ──────────────────────────────────────────────────────────────────

/** Get invoice for a booking */
export const getInvoiceByBooking = (bookingId) => {
  const all = _load(KEYS.invoices) || {};
  return all[bookingId] || null;
};

/** Get invoice by invoiceId */
export const getInvoiceById = (invoiceId) => {
  const all = _load(KEYS.invoices) || {};
  return Object.values(all).find(inv => inv.invoiceId === invoiceId) || null;
};

/** Get all invoices (admin) */
export const getAllInvoices = () => {
  return Object.values(_load(KEYS.invoices) || {});
};

/**
 * Admin saves an invoice for a booking.
 * invoice: { bookingId, invoiceId, date, ownerName, phone, email,
 *            vehicle, regNo, mechanic, duration, services, spareParts,
 *            labour, gst, amount, notes, status }
 */
export const saveInvoice = (invoice) => {
  const all = _load(KEYS.invoices) || {};
  all[invoice.bookingId] = { ...invoice, createdAt: new Date().toISOString() };
  _save(KEYS.invoices, all);
  return all[invoice.bookingId];
};

/** Delete an invoice */
export const deleteInvoice = (bookingId) => {
  const all = _load(KEYS.invoices) || {};
  delete all[bookingId];
  _save(KEYS.invoices, all);
};

// ── PAYMENTS ──────────────────────────────────────────────────────────────────

/** Get payment for an invoice */
export const getPaymentByInvoice = (invoiceId) => {
  const all = _load(KEYS.payments) || {};
  return all[invoiceId] || null;
};

/**
 * Save a payment record after customer pays.
 * payment: { invoiceId, bookingId, method, amount, transactionId, paidAt }
 */
export const savePayment = (payment) => {
  const all = _load(KEYS.payments) || {};
  all[payment.invoiceId] = { ...payment, paidAt: new Date().toISOString() };
  _save(KEYS.payments, all);
  return all[payment.invoiceId];
};

// ── FEEDBACK ──────────────────────────────────────────────────────────────────

/** Get all feedback (for testimonials) */
export const getAllFeedback = () => {
  return _load(KEYS.feedback) || [];
};

/** Get feedback for a specific booking */
export const getFeedbackByBooking = (bookingId) => {
  return (getAllFeedback()).find(f => f.bookingId === bookingId) || null;
};

/**
 * Save customer feedback.
 * feedback: { bookingId, invoiceId, userName, vehicle, rating, review, createdAt }
 */
export const saveFeedback = (feedback) => {
  const all = getAllFeedback();
  // Replace if already exists
  const idx = all.findIndex(f => f.bookingId === feedback.bookingId);
  const entry = { ...feedback, id: feedback.bookingId, createdAt: new Date().toISOString() };
  if (idx >= 0) all[idx] = entry;
  else all.unshift(entry);
  _save(KEYS.feedback, all);
  return entry;
};